// src/Components/Admin/Region.js

import React, { useRef, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';

import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../Firebase/firebase';

// Import Hooks & Components
import { useRegionManage } from '../../Hooks/useRegionManage';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

// Import Icons
import {
  MenuAddNew,
  MenuSave,
  MenuSaveGrey,
  MenuDelete,
  MenuDeleteGrey,
  MenuIconCancel,
  MenuIconCancelGrey,
} from '../../assets/icons/CustomIcons';

// --- STYLING CONSTANTS ---
const ORANGE_COLOR = '#FF9900';
const DARK_BLUE = '#00008B';
const LIGHT_BLUE_BG = '#E3F2FD';

// --- HELPER: Editable Cell (Buffered) ---
const EditableCell = ({
  value,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  placeholder,
  makeUppercase,
}) => {
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState(value || '');

  // Keep local value in sync with external value ONLY when we aren't actively focused on it
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setLocalValue(value || '');
    }
  }, [value]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const insertAtCursor = (textToInsert) => {
    const input = inputRef.current;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const val = input.value;
    const newVal = val.slice(0, start) + textToInsert + val.slice(end);
    setLocalValue(newVal);
    onChange(newVal); // IMMEDIATELY UPDATE PARENT DIRTY STATE
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.selectionStart = inputRef.current.selectionEnd =
          start + textToInsert.length;
      }
    }, 0);
  };

  const handleChange = (e) => {
    setLocalValue(e.target.value);
    onChange(e.target.value); // IMMEDIATELY UPDATE PARENT DIRTY STATE
  };

  const handleKeyDown = (e) => {
    // Pressing Enter commits the edit by forcing a blur
    if (e.key === 'Enter') {
      inputRef.current.blur();
      return;
    }
    if (makeUppercase) {
      // Intercept single character keys to override casing
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const keyLower = e.key.toLowerCase();
        const keyUpper = e.key.toUpperCase();
        // Check if the key is a letter
        if (keyLower !== keyUpper) {
          e.preventDefault();
          // Reverse the shift key logic for capitalization
          const char = e.shiftKey ? keyLower : keyUpper;
          insertAtCursor(char);
        }
      }
    }
  };

  const handlePaste = (e) => {
    if (makeUppercase) {
      e.preventDefault();
      const paste = (e.clipboardData || window.clipboardData)
        .getData('text')
        .toUpperCase();
      insertAtCursor(paste);
    }
  };

  const handleBlur = () => {
    // Commit changes to the parent hook when the cell loses focus
    if (localValue !== (value || '')) {
      onChange(localValue);
    }
    if (onBlur) onBlur(); // Fire blur to clear active focus lock
  };

  return (
    <TextField
      inputRef={inputRef}
      value={localValue}
      placeholder={placeholder}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onClick={(e) => e.stopPropagation()}
      onFocus={onFocus}
      variant='standard'
      size='small'
      fullWidth
      InputProps={{
        disableUnderline: true,
        style: { fontSize: '0.8rem', color: DARK_BLUE, fontWeight: 'normal' },
      }}
      sx={{
        backgroundColor: LIGHT_BLUE_BG,
        px: 1,
        borderRadius: 1,
        border: `1px solid ${ORANGE_COLOR}`,
        boxSizing: 'border-box',
        '& .MuiInputBase-root': {
          height: '100%',
          fontSize: '0.8rem',
          color: DARK_BLUE,
        },
        '& .MuiInputBase-input': {
          color: DARK_BLUE,
          fontWeight: 'normal',
        },
        '&:hover': { backgroundColor: LIGHT_BLUE_BG },
        '&:focus-within': { backgroundColor: LIGHT_BLUE_BG },
      }}
    />
  );
};

export default function Region({ setIsDirty }) {
  // --- HOOK ---
  const { state, flags, alerts, usage, actions } = useRegionManage();

  const [makeUppercase, setMakeUppercase] = useState(true);

  // --- FETCH TEXT FORMATTING PREFERENCE ---
  useEffect(() => {
    const unsubFmt = onSnapshot(doc(db, 'settings', 'TextFormatting'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setMakeUppercase(data.makeUppercase !== undefined ? !!data.makeUppercase : true);
      }
    });
    return () => unsubFmt();
  }, []);

  // --- SYNC DIRTY STATE ---
  useEffect(() => {
    if (setIsDirty) setIsDirty(flags.isDirty);
  }, [flags.isDirty, setIsDirty]);

  // --- SCROLL TO BOTTOM ON ADD ---
  const tableContainerRef = useRef(null);
  useEffect(() => {
    if (state.newRows.length > 0 && tableContainerRef.current) {
      setTimeout(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollTop = tableContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [state.newRows.length]);

  // --- STYLING CONSTANTS ---
  const ICON_SIZE = 64;
  const toolBlockStyle = {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minWidth: 0,
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* --- HEADER --- */}
      <Box
        sx={{
          height: '53px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f4f4f4',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          zIndex: 50,
          px: '5px',
          mb: 2,
        }}>
        {/* ADD */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => actions.handleHeaderAction('ADD_NEW')}>
            <MenuAddNew sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>

        {/* SAVE */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => {
              if (flags.canSave) {
                if (actions.setFocusedRowId) actions.setFocusedRowId(null);
                actions.handleHeaderAction('SAVE');
              }
            }}
            disabled={!flags.canSave}>
            {flags.canSave ? (
              <MenuSave sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuSaveGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        {/* DELETE */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => {
              if (flags.canDelete) {
                if (actions.setFocusedRowId) actions.setFocusedRowId(null);
                actions.handleHeaderAction('DELETE');
              }
            }}
            disabled={!flags.canDelete}>
            {flags.canDelete ? (
              <MenuDelete sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuDeleteGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        {/* CLOSE */}
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => {
              if (flags.canClose) {
                if (actions.setFocusedRowId) actions.setFocusedRowId(null);
                actions.handleHeaderAction('CLOSE');
              }
            }}
            disabled={!flags.canClose}>
            {flags.canClose ? (
              <MenuIconCancel sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuIconCancelGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>
      </Box>

      {/* --- TABLE --- */}
      <TableContainer
        ref={tableContainerRef}
        component={Paper}
        elevation={0}
        sx={{
          flexGrow: 1,
          width: '100%',
          boxSizing: 'border-box',
          // STYLE: Top Border Only (Orange)
          borderTop: '2px solid #ff9800',
          borderBottom: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          borderRadius: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollBehavior: 'smooth',
        }}>
        {state.loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}>
            <CircularProgress sx={{ color: '#ff9800' }} />
          </Box>
        ) : (
          <Table
            stickyHeader
            size='small'
            aria-label='regions table'
            sx={{ width: '100%' }}>
            <TableHead>
              <TableRow
                sx={{
                  height: '36px',
                  '& th': {
                    backgroundColor: '#fff3e0',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #ff9800',
                    py: '4px',
                  },
                }}>
                <TableCell padding='checkbox' sx={{ width: '50px' }} />
                <TableCell sx={{ width: '75%' }}>Region Name</TableCell>
                <TableCell align='center' sx={{ width: '15%' }}>
                  Archived
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {state.displayRegions.map((row) => {
                const isSelected = state.selectedIds.includes(row.id);
                const isFocused = state.focusedRowId === row.id;

                return (
                  <TableRow
                    key={row.id}
                    hover
                    selected={isSelected}
                    onClick={() => actions.handleCheckboxChange(row.id)}
                    sx={{
                      cursor: 'pointer',
                      height: '32px',
                      '& td': { py: '4px' },
                      '&.Mui-selected': { backgroundColor: 'transparent !important' },
                      '&.Mui-selected:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                      },
                    }}>
                    <TableCell padding='checkbox'>
                      <Checkbox
                        size='small'
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => actions.handleCheckboxChange(row.id)}
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>

                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <EditableCell
                        value={row.name}
                        placeholder='Enter Region Name'
                        autoFocus={isFocused}
                        onFocus={() => actions.setFocusedRowId(row.id)}
                        onBlur={() => actions.setFocusedRowId(null)}
                        makeUppercase={makeUppercase}
                        onChange={(val) => actions.handleFieldChange(row.id, 'name', val)}
                      />
                    </TableCell>

                    <TableCell align='center' onClick={(e) => e.stopPropagation()}>
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Checkbox
                          size='small'
                          checked={!!row.isArchived}
                          onChange={(e) =>
                            actions.handleFieldChange(
                              row.id,
                              'isArchived',
                              e.target.checked,
                            )
                          }
                          sx={{ p: 0.5 }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* --- DIALOGS --- */}

      <ConfirmDialog
        open={alerts.showUnsaved}
        onClose={() => alerts.setShowUnsaved(false)}
        onConfirm={actions.handleCloseConfirmed}
        message='Do you want to discard those changes?'
        severity='warning'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus
        sx={{ zIndex: 100005 }}
      />

      <ConfirmDialog
        open={alerts.showDelete}
        onClose={() => alerts.setShowDelete(false)}
        onConfirm={actions.handleConfirmDelete}
        message='This action will permanently delete the selected regions. Are you sure?'
        severity='error'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus
        sx={{ zIndex: 100005 }}
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
        }}
      />

      <ConfirmDialog
        open={alerts.showUsageBlock}
        onClose={() => alerts.setShowUsageBlock(false)}
        onConfirm={() => alerts.setShowUsageBlock(false)}
        message={`You cannot delete this region because it is currently used by ${usage.count} marker(s). Archive it instead`}
        severity='warning'
        confirmText='OK'
        cancelText={null}
        disableRestoreFocus
        sx={{ zIndex: 100005 }}
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
        }}
      />
    </Box>
  );
}
